import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import useAuth from '../hooks/useAuth';

// Centralised basemap tile configuration for every GIS map (MultiMap, Map,
// SurveyTrackingMap, PrePostAnalysis). The app stores a single XYZ raster tile
// URL template — e.g. `https://.../{z}/{x}/{y}.png` — that admins change from
// Settings → Application Settings. The value is org-wide: it's persisted on the
// backend (Organization.map_tile_url), so when one user changes it every user
// in the organization sees the same basemap. localStorage is only a local cache
// to avoid a flash of the default tile before the API responds. Until a custom
// tile is saved, maps keep using the default CARTO light basemap.

// Cache only (source of truth is the backend). Kept in sync after fetch/save.
const STORAGE_KEY = 'mapTileUrl';

// Default basemap — the CARTO "light_all" raster tiles already used across the
// GIS maps. Kept as the fallback / reset target.
export const DEFAULT_TILE_URL = 'https://a.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';

export const DEFAULT_ATTRIBUTION =
    '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors © <a href="https://carto.com/attributions">CARTO</a>';

// Ready-made XYZ sources the user can pick instead of typing a URL by hand.
export const TILE_PRESETS = [
    { label: 'CARTO Light (default)', url: DEFAULT_TILE_URL },
    { label: 'CARTO Dark', url: 'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png' },
    { label: 'CARTO Voyager', url: 'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png' },
    { label: 'OpenStreetMap', url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png' },
    { label: 'Esri World Imagery (Satellite)', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}' },
    { label: 'Esri World Topo', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}' },
    { label: 'OpenTopoMap', url: 'https://a.tile.opentopomap.org/{z}/{x}/{y}.png' },
];

// An XYZ template must contain {z}, {x} and {y} placeholders.
export const isValidXYZ = (url) =>
    typeof url === 'string' &&
    /\{z\}/.test(url) &&
    /\{x\}/.test(url) &&
    /\{y\}/.test(url);

// Build a MapLibre raster style object from a single XYZ tile URL template.
// Shared by every map component so they all render the configured basemap.
export const buildRasterStyle = (tileUrl, attribution = DEFAULT_ATTRIBUTION) => ({
    version: 8,
    sources: {
        basemap: {
            type: 'raster',
            tiles: [tileUrl],
            tileSize: 256,
            attribution,
        },
    },
    layers: [{ id: 'basemap', type: 'raster', source: 'basemap' }],
});

// Auth token, read the same way as the sibling settings contexts.
const getToken = () => {
    try {
        const raw = localStorage.getItem('token');
        return raw ? raw.replace(/^"|"$/g, '') : null;
    } catch (e) {
        return null;
    }
};

const authHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const readStoredTileUrl = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored && isValidXYZ(stored) ? stored : null;
    } catch (e) {
        return null;
    }
};

const MapTileContext = createContext(undefined);

export const useMapTile = () => {
    const context = useContext(MapTileContext);
    if (context === undefined) {
        throw new Error('useMapTile must be used within a MapTileProvider');
    }
    return context;
};

const writeCache = (url) => {
    try {
        if (url) localStorage.setItem(STORAGE_KEY, url);
        else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        /* localStorage unavailable — keep in-memory value only */
    }
};

export const MapTileProvider = ({ children }) => {
    const { isAuthenticated, user } = useAuth();

    // Seed from the cache so maps render immediately, then reconcile with the
    // value the backend returns for the *currently logged-in* user.
    const [tileUrl, setTileUrlState] = useState(readStoredTileUrl() || DEFAULT_TILE_URL);
    // Where the active tile comes from: 'organization' or 'global'. A SuperAdmin
    // in the all-orgs context edits the 'global' default.
    const [scope, setScope] = useState('organization');

    // Whether a custom tile is configured (vs. the application default).
    const isCustom = tileUrl !== DEFAULT_TILE_URL;

    // Re-resolve the tile whenever the logged-in user changes (login, logout, or
    // account switch on the same browser). This is what stops one user's tile
    // from leaking into another user's session via the shared localStorage cache.
    const identity = isAuthenticated ? (user?.email || '') : '';
    useEffect(() => {
        let cancelled = false;

        // Logged out -> drop any cached tile and show the application default.
        if (!identity) {
            setTileUrlState(DEFAULT_TILE_URL);
            setScope('organization');
            writeCache('');
            return undefined;
        }

        (async () => {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_BASE_URL}profile/get_map_settings`,
                    { headers: authHeaders() }
                );
                if (cancelled) return;
                const serverUrl = res?.data?.result?.mapTileUrl;
                const next = serverUrl && isValidXYZ(serverUrl) ? serverUrl : DEFAULT_TILE_URL;
                setTileUrlState(next);
                setScope(res?.data?.result?.scope || 'organization');
                writeCache(next === DEFAULT_TILE_URL ? '' : next);
            } catch (e) {
                // API unreachable: don't trust the shared cache for this user —
                // fall back to the default rather than risk another user's tile.
                if (!cancelled) {
                    setTileUrlState(DEFAULT_TILE_URL);
                    setScope('organization');
                    writeCache('');
                }
            }
        })();
        return () => { cancelled = true; };
    }, [identity]);

    // Persist a new tile org-wide (admin only — enforced by the backend).
    const setTileUrl = useCallback(async (url) => {
        const next = (url || '').trim();
        if (!isValidXYZ(next)) {
            return { success: false, error: 'URL must contain {z}, {x} and {y} placeholders.' };
        }
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_BASE_URL}profile/update_map_settings`,
                { mapTileUrl: next },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
            );
            setTileUrlState(next);
            const savedScope = res?.data?.result?.scope;
            if (savedScope) setScope(savedScope);
            writeCache(next);
            return { success: true, scope: savedScope };
        } catch (e) {
            const apiMsg = e?.response?.data?.error || e?.message || 'Failed to save map tile.';
            return { success: false, error: apiMsg };
        }
    }, []);

    // Clear the org-wide override -> everyone falls back to the default tile.
    const resetTileUrl = useCallback(async () => {
        try {
            const res = await axios.put(
                `${process.env.REACT_APP_BASE_URL}profile/update_map_settings`,
                { mapTileUrl: '' },
                { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
            );
            setTileUrlState(DEFAULT_TILE_URL);
            const savedScope = res?.data?.result?.scope;
            if (savedScope) setScope(savedScope);
            writeCache('');
            return { success: true, scope: savedScope };
        } catch (e) {
            const apiMsg = e?.response?.data?.error || e?.message || 'Failed to reset map tile.';
            return { success: false, error: apiMsg };
        }
    }, []);

    // Raster style built from the active tile — maps can consume this directly.
    const rasterStyle = useMemo(() => buildRasterStyle(tileUrl), [tileUrl]);

    const value = useMemo(
        () => ({ tileUrl, isCustom, scope, rasterStyle, setTileUrl, resetTileUrl }),
        [tileUrl, isCustom, scope, rasterStyle, setTileUrl, resetTileUrl]
    );

    return <MapTileContext.Provider value={value}>{children}</MapTileContext.Provider>;
};

export default MapTileContext;
